import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal } from 'antd';
import API from '../services/api';

/**
 * 单点登录检测Hook
 * 负责检测用户是否在其他设备登录，并处理踢出逻辑
 */
const useSingleSignOn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef(null);
  const lastCheckRef = useRef(Date.now());

  // 清除认证信息
  const clearAuthData = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('accountType');
    localStorage.removeItem('permissions');
    localStorage.removeItem('parentUsername');
  }, []);

  // 检查是否已登录
  const isLoggedIn = useCallback(() => {
    const token = localStorage.getItem('token');
    return !!token;
  }, []);



  // 获取账号类型
  const getAccountType = useCallback(() => {
    return localStorage.getItem('accountType') || 'main';
  }, []);

  // 处理被踢出
  const handleKickOut = useCallback((reason = '您的账号已在其他设备登录', accountType = null) => {
    const currentAccountType = accountType || getAccountType();
    clearAuthData();
    
    Modal.warning({
      title: '登录状态失效',
      content: reason,
      okText: '确定',
      centered: true,
      mask: true,
      maskClosable: false,
      onOk: () => {
        if (currentAccountType === 'ram') {
          navigate('/ram-login');
        } else {
          navigate('/login');
        }
      }
    });
  }, [clearAuthData, getAccountType, navigate]);

  // 心跳检测
  const performHeartbeat = useCallback(async () => {
    if (!isLoggedIn()) {
      return;
    }

    try {
      const response = await API.get('/api/auth/heartbeat');
      
      // 检查响应中是否有会话状态信息
      if (response.data?.sessionStatus === 'kicked') {
        const reason = response.data.message || '您的账号已在其他设备登录';
        handleKickOut(reason, response.data.accountType);
        return;
      }

      // 更新最后检查时间
      lastCheckRef.current = Date.now();
    } catch (error) {
      // 心跳失败，API拦截器会处理401等错误
      // 这里只处理其他类型的错误
      if (error.response?.status !== 401) {
        console.debug('心跳检测失败:', error.message);
      }
    }
  }, [isLoggedIn, handleKickOut]);

  // 启动心跳检测
  const startHeartbeat = useCallback(() => {
    if (!isLoggedIn()) {
      return;
    }

    // 清除之前的定时器
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 立即执行一次心跳检测
    performHeartbeat();

    // 设置定期心跳检测（每30秒）
    intervalRef.current = setInterval(performHeartbeat, 30000);
  }, [isLoggedIn, performHeartbeat]);

  // 停止心跳检测
  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // 页面可见性变化处理
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // 页面隐藏时暂停心跳检测
      stopHeartbeat();
    } else {
      // 页面显示时恢复心跳检测
      if (isLoggedIn()) {
        // 如果离开时间超过1分钟，立即进行一次检测
        const timeSinceLastCheck = Date.now() - lastCheckRef.current;
        if (timeSinceLastCheck > 60000) {
          performHeartbeat();
        }
        startHeartbeat();
      }
    }
  }, [isLoggedIn, startHeartbeat, stopHeartbeat, performHeartbeat]);

  // 监听页面焦点变化
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // 路由变化时重新启动心跳检测
  useEffect(() => {
    // 登录页面不需要心跳检测
    const loginPaths = ['/login', '/ram-login', '/register', '/forgot-password', '/reset-password'];
    const isLoginPage = loginPaths.some(path => location.pathname.startsWith(path));

    if (isLoginPage) {
      stopHeartbeat();
    } else if (isLoggedIn()) {
      startHeartbeat();
    }

    // 组件卸载时清理
    return () => {
      stopHeartbeat();
    };
  }, [location.pathname, isLoggedIn, startHeartbeat, stopHeartbeat]);

  // 监听自定义踢出事件
  useEffect(() => {
    const handleCustomKickOut = (event) => {
      const { reason, accountType } = event.detail || {};
      handleKickOut(reason, accountType);
    };

    window.addEventListener('sso-kick-out', handleCustomKickOut);
    
    return () => {
      window.removeEventListener('sso-kick-out', handleCustomKickOut);
    };
  }, [handleKickOut]);

  return {
    startHeartbeat,
    stopHeartbeat,
    performHeartbeat,
    handleKickOut,
    isLoggedIn
  };
};

export default useSingleSignOn; 